import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormDrawerWrapper from '../../../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../../../features/components/Forms/EditForm/EditSectionsList'
import NameSectionPreview from '../../../ReusableFormSections/NameSection/NameSectionPreview'
import NameSectionForm from './Sections/NameSection'
import AudienceSectionPreview from './Sections/AudienceSection/AudienceSectionPreview'
import AudienceSectionForm from './Sections/AudienceSection/AudienceSectionForm'

import useManageDrawer from '../../../../hooks/formHooks/useManageDrawer'
import useManageEditFormData from '../../../../hooks/formHooks/useManageEditFormData'

import {
  getLineItem,
  clearLineItem,
  getLineItemCriterions,
  clearLineItemCriterions
} from '../../../../modules/actions/lineItems'
import {
  lineItemSelector,
  lineItemLoadingSelector,
  lineItemWasLoadedSelector,
  lineItemCriterionsWasLoadedSelector
} from '../../../../modules/selectors/lineItems'
import { selectedGoogleAdAccountSelector } from '../../../../modules/selectors/adAccounts'

import { LINE_ITEM_GOOGLE_EDIT } from '../../../../constants/forms'
import { NAME } from './fields'
import { GOOGLE_PLATFORM } from '../../../../constants/selectLists/platformList'
import { AUDIENCE } from '../LineItemGoogleCreate/LineItemGoogleCreateForm/LineItemGoogleAudienceForm/fields'

// here defined initial open states for function LineItemGoogleEdit sections
const sectionsInitialOpenStates = {
  [NAME]: false,
  [AUDIENCE]: false
}

function LineItemGoogleEdit({ shouldBeCleared }) {
  const dispatch = useDispatch()

  const item = useSelector(lineItemSelector)
  const isItemLoading = useSelector(lineItemLoadingSelector)
  const itemWasLoaded = useSelector(lineItemWasLoadedSelector)
  const lineItemCriterionsWasLoaded = useSelector(lineItemCriterionsWasLoadedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)

  // to avoid loading representation
  const isInitialDataLoading = isItemLoading || !itemWasLoaded || !lineItemCriterionsWasLoaded

  const getItemDataHandler = useCallback(
    selectedEditItemId => {
      dispatch(getLineItem({ id: selectedEditItemId, account: selectedAdAccountId }, GOOGLE_PLATFORM))
    },
    [dispatch, selectedAdAccountId]
  )

  const clearItemDataHandler = useCallback(() => {
    if (shouldBeCleared) {
      dispatch(clearLineItem())
    }

    dispatch(clearLineItemCriterions())
  }, [dispatch, shouldBeCleared])

  const { isFormOpen, selectedEditItemId: adGroupId } = useManageDrawer({
    formName: LINE_ITEM_GOOGLE_EDIT
  })

  useManageEditFormData({
    formName: LINE_ITEM_GOOGLE_EDIT,
    loadedDataId: item.id,
    getDataHandler: getItemDataHandler,
    clearDataHandler: clearItemDataHandler
  })

  // here defined all edit sections for LineItemGoogleEdit
  const editSections = useMemo(
    () => [
      {
        sectionName: NAME,
        title: 'Name',
        PreviewComponent: <NameSectionPreview dataSelector={lineItemSelector} />,
        FormComponent: <NameSectionForm />
      },
      {
        sectionName: AUDIENCE,
        title: 'Audience',
        PreviewComponent: <AudienceSectionPreview />,
        FormComponent: <AudienceSectionForm />
      }
    ],
    []
  )

  useEffect(() => {
    if (isFormOpen && adGroupId) {
      dispatch(getLineItemCriterions({ ad_group: adGroupId, account: selectedAdAccountId }))
    }
  }, [dispatch, adGroupId, isFormOpen, selectedAdAccountId])

  return (
    <FormDrawerWrapper
      title="Edit the settings of your ad group"
      formName={LINE_ITEM_GOOGLE_EDIT}
      showOpenButton={false}
    >
      <EditSectionsList
        editSections={editSections}
        isEditDataLoading={isInitialDataLoading}
        editItemData={item}
        sectionsInitialOpenStates={sectionsInitialOpenStates}
      />
    </FormDrawerWrapper>
  )
}

export default LineItemGoogleEdit
